// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { vi } from './vi.js';
import { en } from './en.js';
import { da } from './da.js';
import { sv } from './sv.js';
import { nb } from './nb.js';
import { nn } from './nn.js';
import { de } from './de.js';
import { fi } from './fi.js';
import { nl } from './nl.js';
import { fr } from './fr.js';
import { lb } from './lb.js';
import { ga } from './ga.js';
import { es } from './es.js';
import { ca } from './ca.js';
import { eu } from './eu.js';
import { pt } from './pt.js';
/**
 * Keep newly-added Forseti workflow leaves present in every raw locale export.
 * Runtime translations already fall back through `deepMerge`; the raw exports
 * are also consumed by parity tests and a few build-time tools, so apply that
 * same fallback narrowly to this feature until each language pack supplies a
 * localized override. Existing translated values always win.
 */
const FORSETI_WORKFLOW_FALLBACK_KEYS = [
    'availableLabel',
    'builtinGuidance',
    'customBadge',
    'governedWorkflowLabel',
    'howItWorksHeading',
    'humanReviewLabel',
    'libraryDescription',
    'libraryEyebrow',
    'libraryOverviewLabel',
    'libraryTitle',
    'myWorkflowsLabel',
    'openWorkflow',
    'outputsHeading',
    'readyToRunHeading',
    'runCitationWarning',
    'runNoResult',
    'runResultHeading',
    'runViewEvidence',
    'workflowProfileHeading',
];
function applyForsetiWorkflowFallback(locale) {
    const source = en.forseti.workflows;
    const target = locale.forseti.workflows;
    for (const key of FORSETI_WORKFLOW_FALLBACK_KEYS)
        target[key] ??= source[key];
    locale.forseti.advisorRunPanel.workbenchLabel ??= en.forseti.advisorRunPanel.workbenchLabel;
    locale.forseti.advisorRunPanel.currentRecordLabel ??=
        en.forseti.advisorRunPanel.currentRecordLabel;
    const enBreachDraftResolved = en.forseti.advisorRunPanel.breachDraftResolved;
    if (enBreachDraftResolved) {
        if (!locale.forseti.advisorRunPanel.breachDraftResolved) {
            locale.forseti.advisorRunPanel.breachDraftResolved = { ...enBreachDraftResolved };
        }
        else {
            for (const key of Object.keys(enBreachDraftResolved)) {
                locale.forseti.advisorRunPanel.breachDraftResolved[key] ??= enBreachDraftResolved[key];
            }
        }
    }
    locale.forseti.rowAction.shortLabel ??= en.forseti.rowAction.shortLabel;
}
for (const locale of [vi, da, sv, nb, nn, de, fi, nl, fr, lb, ga, es, ca, eu, pt]) {
    applyForsetiWorkflowFallback(locale);
}
export { vi, en, da, sv, nb, nn, de, fi, nl, fr, lb, ga, es, ca, eu, pt };
export { translateEnum } from './translate-enum.js';
const translations = {
    // Vietnamese
    'vi-VN': vi,
    vi: vi,
    // English
    'en-US': en,
    en: en,
    'en-IE': en,
    'en-GB': en,
    // Danish
    'da-DK': da,
    da: da,
    // Swedish
    'sv-SE': sv,
    'sv-FI': sv,
    sv: sv,
    // Norwegian Bokmål
    'nb-NO': nb,
    nb: nb,
    // Norwegian Nynorsk
    'nn-NO': nn,
    nn: nn,
    // German
    'de-DE': de,
    'de-AT': de,
    'de-LU': de,
    'de-BE': de,
    de: de,
    // Finnish
    'fi-FI': fi,
    fi: fi,
    // Dutch
    'nl-NL': nl,
    'nl-BE': nl,
    nl: nl,
    // French
    'fr-FR': fr,
    'fr-BE': fr,
    'fr-LU': fr,
    fr: fr,
    // Luxembourgish
    'lb-LU': lb,
    lb: lb,
    // Irish
    'ga-IE': ga,
    ga: ga,
    // Spanish
    'es-ES': es,
    es: es,
    // Catalan
    'ca-ES': ca,
    ca: ca,
    // Basque
    'eu-ES': eu,
    eu: eu,
    // Portuguese
    'pt-PT': pt,
    pt: pt,
};
/** Deep-merge two objects, using `base` values wherever `overlay` is missing. */
function deepMerge(base, overlay) {
    const result = { ...base };
    for (const key of Object.keys(overlay)) {
        const bVal = base[key];
        const oVal = overlay[key];
        if (typeof bVal === 'object' &&
            bVal !== null &&
            typeof oVal === 'object' &&
            oVal !== null &&
            !Array.isArray(bVal)) {
            result[key] = deepMerge(bVal, oVal);
        }
        else {
            result[key] = oVal;
        }
    }
    return result;
}
const enBase = en;
/**
 * Get translation object for a given locale.
 * Deep-merges with English so any missing keys fall back to EN instead of crashing.
 */
export function getTranslations(locale) {
    const t = translations[locale] ?? translations['en-US'];
    if (t === enBase)
        return t;
    return deepMerge(enBase, t);
}
//# sourceMappingURL=index.js.map